import ListItemLoading from "@/components/loading/listItemLoading/listItemLoading";
import { Customer } from "@/models/response/customer";
import useGetCustomers from "@/pages/dashboard/customer/hooks/useGetCustomers";
import {
  Avatar,
  Divider,
  Drawer,
  Group,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";
import SearchCustomerBox from "../../../components/calendar/SearchCustomer";
import CustomerItem from "./customerItem";
import AddNewCustomer from "./addNewCustomer";
import { useDisclosure } from "@mantine/hooks";

type Props = {
  previewCustomer: boolean;
  closeCustomer: () => void;
  setSelectedCustomer: (customer: Customer | null) => void;
};

function SelectCustomer({
  previewCustomer,
  closeCustomer,
  setSelectedCustomer,
}: Props) {
  const [search, setSearch] = useState("");
  const [opened, { open, close }] = useDisclosure(false);
  const { data, isLoading, refetch } = useGetCustomers({
    term: search,
    count: 100,
    skip: 0,
    enabled: previewCustomer,
  });

  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (stickyRef.current) {
        const rect = stickyRef.current.getBoundingClientRect();
        // Check if the element is stuck (top position is at or below 56px - header height)
        setIsSticky(rect.top <= 56);
      }
    };

    // Find the drawer content area - Mantine Drawer creates a .mantine-Drawer-body element
    const drawerBody = document.querySelector(
      ".mantine-Drawer-content"
    ) as HTMLElement;

    if (drawerBody) {
      drawerBody.addEventListener("scroll", handleScroll);
      return () => drawerBody.removeEventListener("scroll", handleScroll);
    } else {
      // Fallback to window scroll
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const handleSelectCustomer = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    closeCustomer();
  };

  return (
    <>
      <Drawer
        opened={previewCustomer}
        onClose={closeCustomer}
        position="bottom"
        radius="md"
        title={isSticky ? "انتخاب مشتری" : ""}
        size="xl"
        zIndex={101}
        styles={{
          root: {
            padding: 0,
          },
          body: {
            padding: 0,
          },
        }}
      >
        <Stack justify="start" flex={1}>
          <Text fw="bold" size="lg" px="md">
            انتخاب مشتری
          </Text>
          <Stack
            ref={stickyRef}
            styles={{
              root: {
                position: "sticky",
                top: "56px", // Header height to stick below the header
                zIndex: 15, // Higher than header but still allows proper stacking
                backgroundColor: "white",
                transition: "border-bottom 0.2s ease",
                paddingTop: isSticky ? "10px" : "0",
                paddingBottom: isSticky ? "10px" : "0",
                boxShadow: isSticky ? "0 2px 4px rgba(0, 0, 0, 0.1)" : "none",
              },
            }}
          >
            <Stack px="md">
              <SearchCustomerBox setSearch={setSearch} />
            </Stack>
          </Stack>
          <Paper p="md" radius="md">
            <Group justify="space-between">
              <Group justify="space-between" onClick={open}>
                <Avatar color="blue" radius="xl" size="lg">
                  <IconPlus />
                </Avatar>

                <Stack gap="0">
                  <Text size="md">افزودن مشتری جدید</Text>
                </Stack>
              </Group>
            </Group>
          </Paper>
          <Divider />
          {isLoading && <ListItemLoading count={4} />}
          {data?.pages.map((page, pageIndex) => (
            <React.Fragment key={pageIndex}>
              {page?.result?.map((customer, customerIndex) =>
                page?.result?.length === customerIndex + 1 ? (
                  <div>
                    <CustomerItem
                      key={customer?.id}
                      customer={customer}
                      setSelectedCustomer={handleSelectCustomer}
                    />
                  </div>
                ) : (
                  <CustomerItem
                    key={customer?.id}
                    customer={customer}
                    setSelectedCustomer={handleSelectCustomer}
                  />
                )
              )}
            </React.Fragment>
          ))}
        </Stack>
      </Drawer>
      <AddNewCustomer
        opened={opened}
        close={close}
        refetch={refetch}
        setSelectedCustomer={setSelectedCustomer}
        closeCustomers={closeCustomer}
      />
    </>
  );
}

export default SelectCustomer;
